import {
  Checkbox,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import {
  BillsTableColumn,
  BillsTableRow,
  BillsTableRowKeys,
  FiltersBills,
  ORDER_BY_TYPE,
  SelectedRows,
} from "../../../core/models/bills/bills.model";
import { useTranslation } from "react-i18next";
import SuppliesBillsStateContent from "../../SuppliesBillsStateContent/SuppliesBillsStateContent";
import "./BillsTable.scss";
import EnvirormentContent from "../../EnvirormentContent/EnvirormentContent";
import { useNavigate } from "react-router-dom";
import theme from "../../../muiTheme";
import LoadingComponentComponent from "../../UI/LoadingComponentComponent/LoadingComponentComponent";
import { useEffect, useState } from "react";
import usePrevious from "../../../core/hooks/usePrevious";
import { setBillSelectedToLocalStorage } from "../../../core/services/localStorage/localStorage.service";

export interface BillsTableProps {
  billsTableRows: BillsTableRow[];
  handleClickDonwloadBill: (id: string) => Promise<void>;
  selectedRows: SelectedRows[];
  setSelectedRows: React.Dispatch<React.SetStateAction<SelectedRows[]>>;
  hasIdCups: boolean;
  getIdCups: (codeCup: string) => string | undefined;
  isDownloadLoading: boolean;
  orderBy: string;
  setOrderBy: React.Dispatch<React.SetStateAction<string>>;
  sortDirection: ORDER_BY_TYPE;
  setSortDirection: React.Dispatch<React.SetStateAction<ORDER_BY_TYPE>>;
  setFiltersBills: React.Dispatch<React.SetStateAction<FiltersBills>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
}

const BillsTable = ({
  billsTableRows,
  handleClickDonwloadBill,
  selectedRows,
  setSelectedRows,
  hasIdCups,
  getIdCups,
  isDownloadLoading,
  orderBy,
  setOrderBy,
  sortDirection,
  setSortDirection,
  setFiltersBills,
  setPage,
}: BillsTableProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rowId, setRowId] = useState<string[]>([]);
  const prevOrderById = usePrevious(orderBy);
  useEffect(() => {
    if (!isDownloadLoading) {
      setRowId([]);
    }
  }, [isDownloadLoading]);

  const billsTableColumns: BillsTableColumn[] | [] = [
    {
      id: "service",
      orderById: "Entorno",
      label: "Servicio",
      align: "left",
      width: "6%",
      cellContent: (row) => (
        <EnvirormentContent service={row.service} variant={"body1"} />
      ),
    },
    {
      id: "socialReason",
      orderById: "NombreCliente",
      label: "Razón social",
      align: "left",
      width: `${hasIdCups ? "35%" : "18%"}`,
    },
    ...(!hasIdCups
      ? [
          {
            id: "cif",
            orderById: "Identidad",
            label: "CIF",
            align: "left",
            width: "8%",
          },
          {
            id: "cups",
            orderById: "CUPS",
            label: "CUPS",
            align: "left",
            width: "16%",
            cellContent: (bill: BillsTableRow) => (
              <Link
                sx={{ fontWeight: "600" }}
                component="button"
                underline="always"
                onClick={() => {
                  setBillSelectedToLocalStorage(bill);
                  navigate(
                    `${process.env.REACT_APP_SUPPLIES}/${getIdCups(
                      bill.cups
                    )}?origin=bills`
                  );
                }}
              >
                {bill.cups}
              </Link>
            ),
          },
        ]
      : []),
    {
      id: "period",
      orderById: "FechaFactura",
      label: "Periodo",
      align: "left",
      width: `${hasIdCups ? "20%" : "15%"}`,
    },
    {
      id: "amount",
      orderById: "ImporteTotal",
      label: "Importe",
      align: "left",
      width: "8%",
      fontWeight: 700,
    },
    {
      id: "emissionDate",
      orderById: "FechaFactura",
      label: "Emisión",
      align: "left",
      width: "8%",
    },
    {
      id: "state",
      orderById: "Estado_Fac",
      label: "Estado",
      align: "left",
      width: `${hasIdCups ? "8%" : "7%"}`,
      cellContent: (row) => <SuppliesBillsStateContent state={row.state} />,
    },
  ];

  const handleChangeSelectAllChechbox = () => {
    if (isRowsSelectedInPage) {
      setSelectedRows(
        selectedRows.filter(
          ({ id }) => !billsTableRows.some(({ id: idRow }) => idRow === id)
        )
      );
      return;
    }

    setSelectedRows((prevSelectedRows) => [
      ...prevSelectedRows,
      ...billsTableRows.map(({ id, cups, cif, idExcel }) => ({
        id,
        cups,
        cif,
        idExcel,
      })),
    ]);
  };

  const handleChangeCheckbox = (
    event: React.ChangeEvent<HTMLInputElement>,
    { id, cups, cif, idExcel }: BillsTableRow
  ) => {
    if (event.target.checked && !isRowSelected(id)) {
      setSelectedRows([...selectedRows, { id, cups, cif, idExcel }]);
      return;
    }

    setSelectedRows(
      selectedRows.filter(({ id: selectedRowId }) => selectedRowId !== id)
    );
  };

  const handleSortClick = (
    target: any,
    isAscDirection: boolean,
    orderById: string
  ) => {
    if (target.id === orderById) {
      const direction =
        prevOrderById !== orderById ? "asc" : isAscDirection ? "desc" : "asc";
      setOrderBy(orderById!);
      setSortDirection(direction);
      setPage(0);
      setFiltersBills((prevFiltersBills) => ({
        ...prevFiltersBills,
        orderBy: { [orderById!]: direction },
      }));
    }
  };

  const isRowSelected = (id: string) =>
    selectedRows.findIndex(({ id: selectedRowId }) => selectedRowId === id) !==
    -1;
  const isRowsSelectedInPage = billsTableRows.some(({ id }) =>
    isRowSelected(id)
  );

  return (
    <TableContainer className="bills-table">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell align="left" width="auto">
              <Checkbox
                checked={isRowsSelectedInPage}
                indeterminate={isRowsSelectedInPage}
                onChange={handleChangeSelectAllChechbox}
                icon={<span className="t-icon-checkbox-default"></span>}
                indeterminateIcon={
                  <span
                    className={`t-icon t-icon-checkbox-ripple ${
                      isRowsSelectedInPage ? "indeterminate" : ""
                    }`}
                  ></span>
                }
                sx={{ fontSize: "24px" }}
              />
            </TableCell>
            {billsTableColumns.map(({ id, align, width, label, orderById }) => {
              const isOrdered = orderBy === orderById;
              const isAscDirection = sortDirection === "asc";

              return (
                <TableCell key={id} align={align} width={width}>
                  <TableSortLabel
                    active={isOrdered}
                    direction={isOrdered ? sortDirection : "desc"}
                    onClick={({ target }) =>
                      handleSortClick(target, isAscDirection, orderById!)
                    }
                    IconComponent={(props) => (
                      <Typography
                        {...props}
                        component={"span"}
                        id={orderById}
                        className={
                          isAscDirection && isOrdered
                            ? "t-icon-arrow-top"
                            : "t-icon-arrow-down"
                        }
                        sx={{
                          color: "primary.main",
                          marginLeft: ".4rem",
                          fontSize: "20px",
                        }}
                      />
                    )}
                  >
                    <Typography id={orderById}>
                      {t(`Tablas.${label}`)}
                    </Typography>
                  </TableSortLabel>
                </TableCell>
              );
            })}
            <TableCell align="left" width="4%" />
          </TableRow>
        </TableHead>
        <TableBody>
          {!!billsTableRows.length &&
            billsTableRows.map((row, index) => {
              const isSelected = isRowSelected(row.id);
              return (
                <TableRow
                  key={`${row.id}_${index}`}
                  sx={{
                    backgroundColor:
                      (isSelected && theme.palette.selected.light) || "#fff",
                    border: `1px solid ${
                      isSelected ? theme.palette.selected.main : "transparent"
                    }`,
                    position: "relative",
                  }}
                >
                  <TableCell width="auto">
                    <Checkbox
                      checked={isSelected}
                      onChange={(event) => {
                        handleChangeCheckbox(event, row);
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                      key={`${row.id}_checkbox`}
                      icon={<span className="t-icon-checkbox-default"></span>}
                      checkedIcon={
                        <span className="t-icon t-icon-checkbox-checked"></span>
                      }
                      sx={{ fontSize: "24px" }}
                    />
                  </TableCell>
                  {billsTableColumns.map(
                    ({ id, width, align, cellContent, fontWeight }) => {
                      const cellValue = row[id as BillsTableRowKeys];
                      return (
                        <TableCell
                          key={`${row.id}_${id}`}
                          align={align}
                          width={width}
                          sx={{
                            fontWeight: fontWeight,
                            padding: 0,
                          }}
                        >
                          {cellContent ? cellContent(row) : cellValue}
                        </TableCell>
                      );
                    }
                  )}
                  <TableCell align="right" width="4%">
                    <Link
                      component="button"
                      underline="none"
                      onClick={() => {
                        handleClickDonwloadBill(row.id);
                        setRowId((prevRowId) => [...prevRowId, row.id]);
                      }}
                    >
                      <Typography
                        component="span"
                        aria-hidden="true"
                        className="t-icon-download"
                        sx={{ fontSize: "25px" }}
                      />
                    </Link>
                  </TableCell>

                  {isDownloadLoading && rowId.includes(row.id) && (
                    <TableCell sx={{ display: "contents" }}>
                      <LoadingComponentComponent
                        isLoading={isDownloadLoading}
                        opacity={0.5}
                      />
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default BillsTable;
